/**
 * ADR-0046 numerical appendix — every Phase 13 constant, named once.
 *
 * The appendix row tables state the PREDICATE; this file states the NUMBER.
 * `packages/core/src/constants/technical.test.ts` binds every value here to
 * `score-e2e.json`'s `constants` block, which the independent Python generator
 * emitted from what it actually computed with — so the ADR, the generator and
 * this module cannot drift apart silently (§A.12).
 *
 * Exported as `@meridian/core/constants/technical` (ADR-0007, subpath-granular)
 * so `packages/ingest` binds `VOLUME_BASIS_REVISION` as a SYMBOL. A second
 * string literal for the basis identity anywhere in the repo is a defect: the
 * volume gate and Stage 4's payload SQL both compare against it, and two copies
 * make a revision bump silently partial (§A.5).
 */

/* ── §A.1 the composite score ─────────────────────────────────────────── */

/** Usable bars required in the prefix through the evaluation index. */
export const ANALYSIS_MIN_BARS = 50;
/** Formed rows required at the evaluation index (of the 13). */
export const SCORE_MIN_FORMED_ROWS = 6;

/**
 * Band edges, evaluated strongest-first with ALL BOUNDS INCLUSIVE, so `-50`
 * is STRONG SELL and `+50` is STRONG BUY (§A.1). The order is the predicate.
 */
export const SCORE_BANDS = ['STRONG BUY', 'BUY', 'NEUTRAL', 'SELL', 'STRONG SELL'] as const;
export type ScoreBand = (typeof SCORE_BANDS)[number];
export const STRONG_SCORE_BOUND = 50;
export const SCORE_BOUND = 20;

/* ── §A.2 the 13 rows, in appendix order ──────────────────────────────── */

/**
 * Row order IS the contract — all 13 always render, in this order, in both
 * data-source modes (Decision G). An unformed row stays present with weight 0
 * rather than being dropped, or local would carry 12 rows and live 13.
 */
export const TECHNICAL_ROW_KEYS = [
  'rsi',
  'macd',
  'bollingerPctB',
  'stochastic',
  'emaCross',
  'sma50',
  'sma200',
  'parabolicSar',
  'volumeSurge',
  'turbulentWaves',
  'ichimoku',
  'adx',
  'vwap',
] as const;
export type TechnicalRowKey = (typeof TECHNICAL_ROW_KEYS)[number];

/* ── §A.2 formation constants ─────────────────────────────────────────── */

export const RSI_PERIOD = 14;
export const MACD_FAST_PERIOD = 12;
export const MACD_SLOW_PERIOD = 26;
export const MACD_SIGNAL_PERIOD = 9;
export const BOLLINGER_PERIOD = 20;
export const BOLLINGER_STDDEV_MULTIPLE = 2;
export const STOCHASTIC_K_PERIOD = 14;
export const STOCHASTIC_D_PERIOD = 3;
export const STOCHASTIC_SMOOTH_PERIOD = 3;
export const ATR_PERIOD = 14;
export const EMA_FAST_PERIOD = 8;
export const EMA_SLOW_PERIOD = 21;
export const SMA_SHORT_PERIOD = 50;
export const SMA_LONG_PERIOD = 200;
export const SAR_STEP = 0.02;
export const SAR_MAX_ACCELERATION = 0.2;
export const ICHIMOKU_TENKAN_PERIOD = 9;
export const ICHIMOKU_KIJUN_PERIOD = 26;
export const ICHIMOKU_SENKOU_B_PERIOD = 52;
export const ICHIMOKU_DISPLACEMENT = 26;
export const ADX_PERIOD = 14;
export const VOLUME_SURGE_FAST_PERIOD = 5;
export const VOLUME_SURGE_SLOW_PERIOD = 20;

/* ── §A.2 row-predicate boundaries, named once ────────────────────────── */

export const RSI_EXTREME_LOW = 25;
export const RSI_OVERSOLD = 30;
export const RSI_MILD_LOW = 45;
export const RSI_MILD_HIGH = 55;
export const RSI_OVERBOUGHT = 70;
export const RSI_EXTREME_HIGH = 75;

export const PCT_B_EXTREME_LOW = 0.05;
export const PCT_B_LOW = 0.15;
export const PCT_B_HIGH = 0.85;
export const PCT_B_EXTREME_HIGH = 0.95;

export const STOCHASTIC_OVERSOLD = 20;
export const STOCHASTIC_OVERBOUGHT = 80;

export const SURGE_SIGNAL_BOUND = 1.3;
export const SURGE_STRONG_BOUND = 1.5;

export const WAVES_EXTREME_LOW = 0.1;
export const WAVES_LOW = 0.25;
export const WAVES_HIGH = 0.75;
export const WAVES_EXTREME_HIGH = 0.9;

export const ADX_TREND_BOUND = 25;
export const ADX_STRONG_TREND_BOUND = 40;

/** Turbulent Waves EMA layers: 20, 30, … 200 (§A.2 row 10). */
export const TW_FIRST_PERIOD = 20;
export const TW_LAST_PERIOD = 200;
export const TW_PERIOD_STEP = 10;
/** Below this many FORMED layers the row unforms rather than shrinking (D-tw-1). */
export const TW_MIN_FORMED_WAVES = 5;
/** A layer `wp` is formed at index `i` only when `i + 1 >= wp + margin`. */
export const TW_LAYER_FORMED_MARGIN = 5;

/* ── §A.5 volume convention ───────────────────────────────────────────── */

/**
 * The canonical stored basis identity. Ingest binds this symbol; Stage 4's
 * payload SQL compares against it. Never write the string twice.
 */
export const VOLUME_BASIS_REVISION = 'v1-unadjusted-share-count';

/* ── §A.7 synthetic envelope ──────────────────────────────────────────── */

/** Wick width as a fraction of the absolute close-to-close move. */
export const SYNTHETIC_OHLC_WICK_ALPHA = 0.5;
export const SYNTHETIC_VOLUME_FLOOR = 1000;
export const SYNTHETIC_VOLUME_BASE = 1_000_000;
export const SYNTHETIC_VOLUME_GAMMA = 3;
/** Median |relative move| over the committed universe — a measurement, not a round number. */
export const SYNTHETIC_VOLUME_R_REF = 0.02;

/* ── §A.8 score history ───────────────────────────────────────────────── */

/** `K` — sparkline resolution and the `k` in every §A.11 budget. */
export const SCORE_HISTORY_SAMPLES = 60;

/* ── §A.3 fixture comparison tolerance ────────────────────────────────── */

/* Decimal TEXT, exactly as the generator emitted it, so the projection check
   compares like for like rather than a re-parsed double. */
export const TOLERANCE_RELATIVE = '5e-12';
export const TOLERANCE_ABSOLUTE = '5e-12';
export const TOLERANCE_ABSOLUTE_WHEN_ABS_LT = '1e-12';

/* ── §A.10 ladder generation ──────────────────────────────────────────── */

export const PIVOT_HALF_WINDOW = 5;
/** A pivot at `p` is usable at `i` only when `p + lag <= i` (Decision F rule 3). */
export const PIVOT_CONFIRMATION_LAG = 5;
/** Cluster width as a fraction of the adjusted close at the evaluation index. */
export const CLUSTER_TOLERANCE_FRACTION = 0.015;
export const CLUSTER_MIN_SIZE = 2;
export const SR_MAX_LEVELS = 6;
/** Ladder rows excluding the CURRENT PRICE banner. */
export const LADDER_MAX_ROWS = 12;
export const FIB_LOOKBACK = 120;
export const FIB_RATIOS = [0.382, 0.618] as const;
export const ATR_STOP_MULTIPLE = 2;
export const LADDER_SMA_PERIODS = [SMA_SHORT_PERIOD, SMA_LONG_PERIOD] as const;

/* ── §Decision H comparison outcomes ──────────────────────────────────── */

/**
 * Closed (ADR-0046 L138). `core` only — this must never reach the wire: it is
 * subject-dependent by construction and the OHLC route has no subject.
 */
export const COMPARISON_UNAVAILABLE_REASONS = [
  'ohlc_unavailable',
  'formed_row_set_mismatch',
  'endpoint_mismatch',
  'not_in_served_set',
] as const;
export type ComparisonUnavailableReason = (typeof COMPARISON_UNAVAILABLE_REASONS)[number];
